/**
 * Simple AG Grid Utilities
 *
 * Minimal helper functions for AG Grid integration.
 * Meets SIMPLER.md requirements for AG Grid utilities.
 */

import get from "lodash/get";

import type {
  EditableCallbackParams,
  GridApi,
  IRowNode,
} from "ag-grid-community";
import type {
  BenefitDefinition,
  RefreshCellConfig,
} from "../../../benefits/benefit.types";
import type { PlanEditorRowData } from "../types/values.types";

export interface CellReference {
  /** Row ID */
  rowId: string;
  /** Column field name */
  field: string;
}

/** Reset operation for dependent cells */
export interface CellResetOperation {
  benefitKey: string;
  propertyKey?: string;
  value: unknown;
}

/**
 * Find a row node by benefitKey
 * @internal - Helper for other utility functions
 */
const findNodeByBenefitKey = (
  api: GridApi,
  benefitKey: string,
): IRowNode<PlanEditorRowData> | null => {
  let found: IRowNode<PlanEditorRowData> | null = null;
  api.forEachNode((node) => {
    if (node.data?.benefitKey === benefitKey) {
      found = node;
      return true; // early exit
    }
    return undefined;
  });
  return found;
};

/**
 * Get value from a specific cell
 */
export const getCellValue = (
  api: GridApi,
  field: string,
  currentRowData?: Record<string, unknown>,
): unknown => {
  // First try to get from current row data (faster)
  if (currentRowData && field in currentRowData) {
    return currentRowData[field];
  }

  // Fallback to API lookup (slower but more reliable)
  let value: unknown;
  api.forEachNode((node) => {
    if (node.data && node.data === currentRowData) {
      value = node.data[field];
      return true; // early exit
    }
    return undefined;
  });

  return value;
};

/**
 * Get value from another row
 */
export const getCellValueFromRow = (
  api: GridApi,
  rowId: string,
  field: string,
): unknown => {
  let value: unknown;

  api.forEachNode((node) => {
    if (node.data?.id === rowId || node.id === rowId) {
      value = node.data?.[field];
      return true; // early exit
    }
    return undefined;
  });

  return value;
};

/**
 * Refresh specific cells in the grid
 */
export const refreshCells = (api: GridApi, cells: CellReference[]): void => {
  if (cells.length === 0) return;

  // Group cells by row for efficient refresh
  const cellsByRow = new Map<string, string[]>();

  cells.forEach(({ rowId, field }) => {
    if (!cellsByRow.has(rowId)) {
      cellsByRow.set(rowId, []);
    }
    cellsByRow.get(rowId)!.push(field);
  });

  // Refresh each row's cells
  cellsByRow.forEach((fields, rowId) => {
    const rowNode = api.getRowNode(rowId);
    if (rowNode) {
      api.refreshCells({
        force: true,
        suppressFlash: true,
        rowNodes: [rowNode],
        columns: fields,
      });
    }
  });
};

/**
 * Get all row data from the grid
 */
export const getAllRowData = (api: GridApi): Record<string, unknown>[] => {
  const rowData: Record<string, unknown>[] = [];
  api.forEachNode((node) => {
    if (node.data) {
      rowData.push(node.data);
    }
  });
  return rowData;
};

/**
 * Find row by ID
 */
export const findRowById = (
  api: GridApi,
  rowId: string,
): Record<string, unknown> | null => {
  let foundRow: Record<string, unknown> | null = null;

  api.forEachNode((node) => {
    if (node.data?.id === rowId || node.id === rowId) {
      foundRow = node.data as Record<string, unknown>;
      return true; // early exit
    }
    return undefined;
  });

  return foundRow;
};

/**
 * Helper to get row ID from AG Grid node/data
 */
export const getRowId = (nodeOrData: unknown): string | undefined => {
  if (!nodeOrData || typeof nodeOrData !== "object") {
    return undefined;
  }

  const data = nodeOrData as Record<string, unknown>;

  // Try node.id first
  if (typeof data["id"] === "string") {
    return data["id"];
  }

  // Try data.id (for nested data structure)
  if (data["data"] && typeof data["data"] === "object") {
    const nestedData = data["data"] as Record<string, unknown>;
    if (typeof nestedData["id"] === "string") {
      return nestedData["id"];
    }
  }

  return undefined;
};

/**
 * Get value from a different row in the same column
 *
 * Useful for cross-row dependencies within the same benefit plan (column)
 * Example: currentCarrierName cell needs to check policyPeriod value in the same plan
 *
 * @param api - Grid API instance
 * @param columnField - The field/column identifier (e.g., "plan-1.inNetwork")
 * @param targetBenefitKey - The benefitKey of the row to get the value from (e.g., "policyPeriod")
 * @returns The value from the specified row in the same column, or undefined if not found
 */
export const getColumnValueFromRow = (
  api: GridApi,
  columnField: string,
  targetBenefitKey: string,
): unknown => {
  const node = findNodeByBenefitKey(api, targetBenefitKey);
  return get(node, `data.${columnField}`);
};

/**
 * Get values from multiple rows in the same column
 *
 * @param api - Grid API instance
 * @param columnField - The field/column identifier
 * @param benefitKeys - Array of benefitKeys to retrieve values for
 * @returns Map of benefitKey to value
 */
export const getColumnValuesFromRows = (
  api: GridApi,
  columnField: string,
  benefitKeys: string[],
): Map<string, unknown> => {
  const values = new Map<string, unknown>();
  const keysSet = new Set(benefitKeys);

  api.forEachNode((node) => {
    const benefitKey = node.data?.benefitKey as string;
    if (benefitKey && keysSet.has(benefitKey)) {
      values.set(benefitKey, node.data[columnField]);
      // Early exit if we've found all requested keys
      if (values.size === benefitKeys.length) {
        return true;
      }
    }
    return undefined;
  });

  return values;
};

/**
 * Get value from a specific benefitKey in the current cell's column
 *
 * This is useful for cells that need to check another benefit's value
 * in the same plan/network column to determine their own behavior.
 *
 * @param params - AG Grid cell params containing api and column info
 * @param targetBenefitKey - The benefitKey of the row to get the value from
 * @returns The value from the target row in the current column, or undefined
 *
 * @example
 * // In an editable function for "individualDeductibleAmount" cell:
 * const typeValue = getCellValueInCurrentColumn(params, "individualDeductibleType");
 * if (typeValue === "notIncluded") return false; // disable this cell
 */
export const getCellValueInCurrentColumn = (
  params: {
    api: GridApi;
    colDef?: { field?: string };
    data?: { benefit?: { networkDependent?: boolean } };
  },
  targetBenefitKey: string,
): unknown => {
  if (!params.api || !params.colDef?.field) {
    return undefined;
  }

  let columnField = params.colDef.field;

  // columnField includes network info if coverage has network like dental ppo
  // remove network suffix for non-network-dependent benefits
  const networkDependent = params.data?.benefit?.networkDependent ?? false;
  if (!networkDependent && columnField.includes(".")) {
    columnField = columnField.split(".")[0];
  }
  return getColumnValueFromRow(params.api, columnField, targetBenefitKey);
};

/**
 * Creates an editable function that disables a cell based on another cell's value
 *
 * This is a reusable factory function for creating conditional editability logic.
 * Use this when a cell should be disabled if another benefit has specific values.
 *
 * @param dependsOnBenefitKey - The benefitKey to check
 * @param disabledValues - Array of values that should disable this cell
 * @returns An editable function compatible with AG Grid's ColDef
 *
 * @example
 * // In cell.configurations.ts:
 * dollarAmount: {
 *   // ...other config
 *   editable: createConditionalEditable("individualDeductibleType", ["notIncluded", "notOffered"])
 * }
 *
 * @example
 * // Usage in benefit definition:
 * individualDeductibleAmount: {
 *   cell: { ...CELL_SPECS["dollarAmount"],
 *           editable: createConditionalEditable("individualDeductibleType", ["notIncluded"]) }
 * }
 */
export const createConditionalEditable =
  (
    dependsOnBenefitKey: string,
    disabledValues: string[] = ["notIncluded", "notOffered"],
  ) =>
  (params: EditableCallbackParams<PlanEditorRowData>): boolean => {
    // First check standard read-only flag
    if (!params.data?.benefit || params.data.benefit.isReadOnly) {
      return false;
    }

    // Check the dependent cell's value in the same column
    const dependentValue = getCellValueInCurrentColumn(
      params,
      dependsOnBenefitKey,
    );

    // Disable if the dependent value is in the disabled list
    if (
      dependentValue !== undefined &&
      disabledValues.includes(dependentValue as string)
    ) {
      return false;
    }

    // Default to editable
    return true;
  };

/**
 * Creates an editable function that disables a cell based on a context field
 *
 * This is a reusable factory function for creating conditional editability logic.
 * Use this when a cell should be disabled if a context field has a specific value.
 *
 * The function checks for context in two places:
 * 1. Column's own context (for single-tier/no-grouping strategies)
 * 2. Parent column group's context (for multi-tier strategies with plan options/networks)
 *
 * This allows the same editable function to work across different column grouping strategies.
 *
 * @param dependsOnContextKey - The context or benefit key to check (e.g., "policyPeriod")
 * @param disabledValues - Array of values that should disable this cell
 * @returns An editable function compatible with AG Grid's ColDef
 *
 * @example
 * // Disable commission fields when policy period is not "current"
 * editable: createContextConditionalEditable("policyPeriod", ["newBusiness", "alternate"])
 *
 * @example
 * // Usage in cell spec:
 * export const currentCommissionType: CellSpec = {
 *   editable: createContextConditionalEditable("policyPeriod", [
 *     BaseBenefitPlanPolicyPeriod.newBusiness,
 *     BaseBenefitPlanPolicyPeriod.alternate,
 *   ]),
 *   // ...other config
 * }
 */
export const createContextConditionalEditable =
  (
    dependsOnContextKey: string,
    disabledValues: string[] = ["notIncluded", "notOffered"],
  ) =>
  (params: EditableCallbackParams): boolean => {
    // First check standard read-only flag
    if (!params.data?.benefit || params.data.benefit.isReadOnly) {
      return false;
    }

    // Try to get context from the column itself first (for no-grouping strategy)
    let context = params.colDef?.context as Record<string, unknown> | undefined;

    // If not found, traverse up to the root column group to find context
    if (!context && params.column) {
      let currentGroup = params.column.getParent();
      while (currentGroup && !context) {
        context = currentGroup.getColGroupDef()?.context as
          | Record<string, unknown>
          | undefined;
        currentGroup = currentGroup.getParent();
      }
    }

    // If still no context, default to editable
    if (!context) {
      return true;
    }

    const dependentValue = context?.[dependsOnContextKey] as string;

    // Disable if the dependent value is in the disabled list
    if (
      dependentValue !== undefined &&
      disabledValues.includes(dependentValue)
    ) {
      return false;
    }

    // Default to editable
    return true;
  };

const isStringArray = (v: string[] | RefreshCellConfig[]): v is string[] =>
  v.length === 0 || typeof v[0] === "string";

/**
 * Resolve a refreshesCells config to a plain array of benefitKeys,
 * applying per-cell value filters when the config form is used.
 */
const resolveRefreshCells = (
  refreshesCells: string[] | RefreshCellConfig[],
  newValue?: unknown,
): string[] => {
  if (isStringArray(refreshesCells)) return refreshesCells;
  return refreshesCells
    .filter(({ values }) => values === undefined || values.includes(newValue))
    .map(({ cell }) => cell);
};

/**
 * Refresh cells that depend on the current cell's value
 *
 * When a cell changes (like a "Type" field), this function refreshes
 * dependent cells (like "Amount" fields) in the same column so they
 * can re-evaluate their editability or display.
 *
 * @param api - Grid API instance
 * @param columnField - The current column field (e.g., "plan-1.inNetwork")
 * @param refreshesCells - BenefitKeys to refresh, or a config with an optional value filter
 * @param newValue - The new cell value; used to apply the value filter when config form is used
 *
 * @example
 * // In onCellValueChanged handler:
 * if (data.benefit?.refreshesCells) {
 *   refreshDependentCells(params.api, colKey, data.benefit.refreshesCells, coercedValue);
 * }
 */
export const refreshDependentCells = (
  api: GridApi,
  columnField: string,
  refreshesCells: string[] | RefreshCellConfig[],
  newValue?: unknown,
): void => {
  const cells = resolveRefreshCells(refreshesCells, newValue);
  if (cells.length === 0) {
    return;
  }

  const cellsToRefresh: CellReference[] = cells.map((benefitKey) => ({
    rowId: benefitKey,
    field: columnField,
  }));

  refreshCells(api, cellsToRefresh);
};

/**
 * Get the default value for a field when it becomes enabled
 *
 * Checks in order:
 * 1. Benefit definition's defaultValue (explicit override)
 * 2. Cell spec's defaultValue (type-level override)
 * 3. Infer from cellEditor or cellType
 * 4. Falls back to 0
 *
 * @param benefitDefinition - The benefit definition containing cell spec and optional defaultValue
 * @returns The default value to use
 */
export const getDefaultValue = (benefitDefinition?: {
  defaultValue?: unknown;
  cell?: {
    defaultValue?: unknown;
    cellEditor?: string;
    cellType?: string;
    dataType?: string;
  };
}): unknown => {
  // Check benefit definition first (highest priority)
  if (benefitDefinition?.defaultValue !== undefined) {
    return benefitDefinition.defaultValue;
  }

  // Check cell spec's explicit defaultValue
  if (benefitDefinition?.cell?.defaultValue !== undefined) {
    return benefitDefinition.cell.defaultValue;
  }

  // Infer from cell type
  const cell = benefitDefinition?.cell;
  if (!cell) return 0; // fallback if no cell spec

  // Check cellEditor first (most specific)
  const cellEditor = cell.cellEditor;
  if (cellEditor === "agNumberCellEditor") {
    return 0;
  }
  if (cellEditor === "agTextCellEditor") {
    return "";
  }
  if (cellEditor === "agCheckboxCellEditor") {
    return false;
  }
  if (cellEditor === "agRichSelectCellEditor") {
    return "notIncluded";
  }

  // Fallback to cellType if cellEditor not present
  const cellType = cell.cellType;
  if (cellType === "number") {
    return 0;
  }
  if (cellType === "text") {
    return "";
  }
  if (cellType === "boolean") {
    return false;
  }
  if (cellType === "dropdown") {
    return "notIncluded";
  }

  // Final fallback
  return 0;
};

/**
 * Set a cell value in the current column and handle dependent cell initialization
 *
 * This function sets a cell value and automatically initializes any dependent
 * cells that need default values when they become enabled.
 *
 * @param api - Grid API instance
 * @param benefitKey - The benefitKey of the row to update
 * @param columnField - The column field identifier (e.g., "plan-1.inNetwork")
 * @param value - The value to set
 *
 * @example
 * // Set a type field that enables amount fields:
 * setCell(api, 'diagnosisBenefitType', 'plan-1.inNetwork', 'dollarAmount');
 *
 * @example
 * // Set an amount field with a default value:
 * setCell(api, 'diagnosisBenefitAmount', 'plan-1.inNetwork', 0);
 */
export const setCell = (
  api: GridApi,
  benefitKey: string,
  columnField: string,
  value: unknown,
): void => {
  const rowNode = api.getRowNode(
    benefitKey,
  ) as IRowNode<PlanEditorRowData> | null;
  if (!rowNode) return;

  // Get the old value before updating
  const oldValue = rowNode.data?.[columnField] as unknown;

  // Set the cell value
  rowNode.setDataValue(columnField, value);

  // Handle dependent cells if this benefit has refreshesCells
  const benefit = rowNode.data?.benefit;
  if (benefit?.refreshesCells) {
    clearDependentCellValues(
      api,
      benefitKey,
      oldValue,
      value,
      benefit.refreshesCells,
      columnField,
    );
  }
};

/**
 * Handle cell variant transitions when a parent cell changes
 * @internal - Helper for clearDependentCellValues
 */
const handleCellVariantTransition = (
  dependentBenefit: BenefitDefinition,
  currentBenefitKey: string,
  newValue: unknown,
  dependentBenefitKey: string,
  propertyKey?: string,
): CellResetOperation | null => {
  const cellVariants = dependentBenefit.cellVariants;
  const hasCellVariants =
    cellVariants &&
    cellVariants.dependsOn === currentBenefitKey &&
    cellVariants.options;

  if (!hasCellVariants) return null;

  // When cell spec changes (e.g., dollar ↔ percent), reset to new cell spec's default
  const newCellSpec =
    cellVariants.options[newValue as string] || cellVariants.default;

  const defaultValue =
    newCellSpec.defaultValue !== undefined
      ? newCellSpec.defaultValue
      : dependentBenefit.defaultValue !== undefined
        ? dependentBenefit.defaultValue
        : 0;

  return {
    benefitKey: dependentBenefitKey,
    propertyKey,
    value: defaultValue,
  };
};

/**
 * Handle editableWhen transitions when a parent cell changes
 * @internal - Helper for clearDependentCellValues
 */
const handleEditableWhenTransition = (
  dependentBenefit: BenefitDefinition,
  currentBenefitKey: string,
  oldValue: unknown,
  newValue: unknown,
  dependentBenefitKey: string,
  propertyKey?: string,
): CellResetOperation | null => {
  const editableWhen = dependentBenefit.editableWhen;

  if (!editableWhen || editableWhen.dependsOn !== currentBenefitKey) {
    return null;
  }

  // Check if this is transitioning between disabled and enabled states.
  // Treat null, undefined, and empty string as "disabled" — an unset parent
  // type field means the dependent field is not active, so switching from
  // unset to an enabled value must apply the default (e.g., selecting
  // "Dollar Amount" from an empty type dropdown should populate the amount).
  const wasDisabled =
    oldValue == null ||
    oldValue === "" ||
    editableWhen.disabledValues.includes(oldValue as string);
  const isNowDisabled =
    newValue == null ||
    newValue === "" ||
    editableWhen.disabledValues.includes(newValue as string);

  // When transitioning between disabled/enabled states (XOR), apply default value
  // This ensures that:
  // 1. Disabled fields get reset to defaults (prevent stale values)
  // 2. Enabled fields get populated with defaults (prevent missing required fields)
  if (isNowDisabled === wasDisabled) return null;

  const defaultValue =
    editableWhen.resetValue !== undefined
      ? editableWhen.resetValue
      : dependentBenefit.defaultValue !== undefined
        ? dependentBenefit.defaultValue
        : dependentBenefit.cell?.defaultValue !== undefined
          ? dependentBenefit.cell.defaultValue
          : 0;

  return {
    benefitKey: dependentBenefitKey,
    propertyKey,
    value: defaultValue,
  };
};

/**
 * Determine which dependent cells need to be reset when a parent cell changes
 *
 * Handles dynamic cell spec switching (cellVariants) when type fields change.
 * For example, when a type changes from "dollarAmount" to "percentage", the
 * value cell needs to reset to the new cell spec's default value.
 *
 * Also identifies dependent cells that should be reset to defaults when disabled.
 *
 * Cascades: when a dependent cell is itself reset, and that dependent cell
 * declares its own `refreshesCells` (e.g. a type field resetting to
 * "notIncluded" that in turn gates an amount field), this recurses using the
 * dependent's actual pre-reset value as `oldValue` so multi-hop chains (toggle
 * → type → amount) clear all the way down instead of stopping one level in.
 *
 * IMPORTANT: This function does NOT update the grid cells directly (no setDataValue).
 * It only returns the reset operations that the caller should apply to the coverage object.
 * The grid cells will be out of sync temporarily, but formatters show "-" for disabled fields.
 * On next load from API, the grid will sync up correctly.
 *
 * @param api - Grid API instance
 * @param columnField - The current column field (e.g., "plan-1.inNetwork")
 * @param currentBenefitKey - The benefitKey of the cell that just changed
 * @param oldValue - The previous value of the cell that just changed
 * @param newValue - The new value of the cell that just changed
 * @param refreshesCells - Array of benefitKeys that depend on the current cell
 * @param colKey - The column field of the cell that changed, used to look up a
 *   cascaded dependent's own pre-reset value; omit to skip cascading (e.g. from
 *   callers/tests with no column-scoped row data).
 * @param visited - @internal - benefitKeys already processed in this cascade, guards against cycles
 * @returns Array of reset operations with benefitKey, propertyKey, and value that need to be applied to coverage
 */
export const clearDependentCellValues = (
  api: GridApi,
  currentBenefitKey: string,
  oldValue: unknown,
  newValue: unknown,
  refreshesCells: string[] | RefreshCellConfig[],
  colKey?: string,
  visited: Set<string> = new Set(),
): CellResetOperation[] => {
  if (visited.has(currentBenefitKey)) return [];
  visited.add(currentBenefitKey);

  const cells = resolveRefreshCells(refreshesCells, newValue);
  if (cells.length === 0 || newValue === undefined) {
    return [];
  }

  const resets: CellResetOperation[] = [];

  cells.forEach((dependentBenefitKey) => {
    const rowNode = api.getRowNode(
      dependentBenefitKey,
    ) as IRowNode<PlanEditorRowData> | null;
    if (!rowNode || !rowNode.data) return;

    const dependentBenefit = rowNode.data.benefit;
    if (!dependentBenefit) return;

    // Check if this dependent cell has cellVariants (dynamic cell spec switching)
    const reset =
      handleCellVariantTransition(
        dependentBenefit,
        currentBenefitKey,
        newValue,
        dependentBenefitKey,
        rowNode.data.propertyKey,
      ) ??
      // Check if this dependent cell has editableWhen configuration
      handleEditableWhenTransition(
        dependentBenefit,
        currentBenefitKey,
        oldValue,
        newValue,
        dependentBenefitKey,
        rowNode.data.propertyKey,
      );

    if (!reset) return;
    resets.push(reset);

    if (dependentBenefit.refreshesCells && colKey) {
      const dependentOldValue = (rowNode.data as Record<string, unknown>)[
        colKey
      ];
      resets.push(
        ...clearDependentCellValues(
          api,
          dependentBenefitKey,
          dependentOldValue,
          reset.value,
          dependentBenefit.refreshesCells,
          colKey,
          visited,
        ),
      );
    }
  });

  return resets;
};
